/* ============================================================
   ÉPRIS Wishlist — Core
   ============================================================
   Architektur:
   - LocalStorage als primary store (synchron, instant)
   - Items als full snapshot {variant_id, handle, title, image, price, url, ...}
     → Drawer rendert ohne extra fetch, survives reload
   - Customer-Metafield (optional, server-side rendered in
     window.eprisCustomerWishlist als ID-Liste) → wird bei Init mit
     LocalStorage gemerged (Union by variant_id)
   - Customer-Metafield-WRITE: optional via Customer Account API oder
     App-Proxy (siehe syncToCustomer() — derzeit no-op fallback)
   - Pub-Sub für UI-Updates: Hearts, Header-Counter, Drawer, Page

   Public API: window.eprisWishlist
     .has(variantId)               → boolean
     .add(variantId, snapshot)     → void  (snapshot = {handle, title, image, price, url, ...})
     .remove(variantId)            → void
     .toggle(variantId, snapshot)  → boolean (new state)
     .getAll()                     → Array<snapshot> (volle Daten)
     .getIds()                     → Array<string>  (nur variant_ids)
     .count()                      → number
     .clear()                      → void
     .onChange(fn)                 → unsubscribe (fn(items, action, variantId))

   Events on document:
     'epris:wishlist:change'  detail: {items, action, variantId}
     'epris:wishlist:added'   detail: {variantId, items}
     'epris:wishlist:removed' detail: {variantId, items}
   ============================================================ */

(function() {
  'use strict';

  const STORAGE_KEY = 'epris_wishlist_v1';
  const MAX_ITEMS = 100; /* sanity limit */
  const listeners = new Set();
  let items = []; /* Array<{variant_id, handle, title, image, price, url, ...}> */

  /* ---------- Storage Layer ---------- */
  function readLocal() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = JSON.parse(raw || '[]');
      if (!Array.isArray(parsed)) return [];
      /* Backward-compat: alte Versionen gespeichert nur strings (variant_ids) */
      return parsed
        .map(entry => {
          if (typeof entry === 'string' || typeof entry === 'number') {
            return { variant_id: String(entry) };
          }
          if (entry && typeof entry === 'object' && entry.variant_id) {
            return Object.assign({}, entry, { variant_id: String(entry.variant_id) });
          }
          return null;
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function writeLocal() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) { /* quota — silently fail */ }
  }

  /* ---------- Customer-Metafield-Sync ---------- */
  function syncFromCustomer() {
    const customerIds = window.eprisCustomerWishlist;
    if (!Array.isArray(customerIds) || customerIds.length === 0) return false;
    const localIds = new Set(items.map(i => i.variant_id));
    let added = false;
    customerIds.map(String).forEach(id => {
      if (!localIds.has(id)) {
        items.push({ variant_id: id }); /* nur ID, snapshot fehlt → wird beim ersten Heart-Render auf der Page nachgeholt */
        added = true;
      }
    });
    if (added) writeLocal();
    return added;
  }

  function syncToCustomer() {
    /* TODO: Customer Account API mutation
       customerUpdate(input: { metafields: [{ namespace: "custom",
       key: "wishlist", type: "list.variant_reference", value: ... }] })
       Requires: Customer Account API endpoint configured (siehe README).
       Fallback: LocalStorage als source of truth, customer-merge passiert
       on next page-load via syncFromCustomer(). */
  }

  /* ---------- Notify ---------- */
  function notify(action, variantId) {
    const snapshot = items.slice();
    listeners.forEach(fn => {
      try { fn(snapshot, action, variantId); } catch (e) { console.error('[wishlist] listener', e); }
    });
    document.dispatchEvent(new CustomEvent('epris:wishlist:change', {
      detail: { items: snapshot, action, variantId }
    }));
    if (action === 'add') {
      document.dispatchEvent(new CustomEvent('epris:wishlist:added', {
        detail: { variantId, items: snapshot }
      }));
    } else if (action === 'remove') {
      document.dispatchEvent(new CustomEvent('epris:wishlist:removed', {
        detail: { variantId, items: snapshot }
      }));
    }
  }

  /* ---------- Cross-Tab-Sync ---------- */
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    items = readLocal();
    notify('sync');
  });

  /* ---------- Public API ---------- */
  window.eprisWishlist = {
    has(variantId) {
      const id = String(variantId);
      return items.some(i => i.variant_id === id);
    },
    add(variantId, snapshot) {
      const id = String(variantId);
      if (!id) return;
      const existing = items.find(i => i.variant_id === id);
      if (existing) {
        /* Update snapshot mit frischen Daten falls mitgegeben */
        if (snapshot) Object.assign(existing, snapshot, { variant_id: id });
        writeLocal();
        return;
      }
      const entry = Object.assign({ variant_id: id }, snapshot || {});
      entry.variant_id = id; /* sicher: ID nicht überschrieben */
      items.unshift(entry); /* neueste oben */
      if (items.length > MAX_ITEMS) items.length = MAX_ITEMS;
      writeLocal();
      syncToCustomer();
      notify('add', id);
    },
    remove(variantId) {
      const id = String(variantId);
      const idx = items.findIndex(i => i.variant_id === id);
      if (idx === -1) return;
      items.splice(idx, 1);
      writeLocal();
      syncToCustomer();
      notify('remove', id);
    },
    toggle(variantId, snapshot) {
      if (this.has(variantId)) {
        this.remove(variantId);
        return false;
      } else {
        this.add(variantId, snapshot);
        return true;
      }
    },
    /* Update snapshot data ohne add/remove (z.B. wenn user Product-Page besucht
       und wir frische Daten haben für item das schon in Wishlist ist).
       WICHTIG: Nur notify wenn sich tatsächlich was ändert — verhindert
       Infinite-Loop wenn refresh aus MutationObserver getriggert wird. */
    updateSnapshot(variantId, snapshot) {
      const id = String(variantId);
      const existing = items.find(i => i.variant_id === id);
      if (!existing || !snapshot) return;
      let changed = false;
      for (const k in snapshot) {
        if (snapshot[k] != null && existing[k] !== snapshot[k]) {
          existing[k] = snapshot[k];
          changed = true;
        }
      }
      if (!changed) return;
      writeLocal();
      notify('update', id);
    },
    getAll() {
      return items.slice();
    },
    getIds() {
      return items.map(i => i.variant_id);
    },
    count() {
      return items.length;
    },
    clear() {
      if (items.length === 0) return;
      items = [];
      writeLocal();
      syncToCustomer();
      notify('clear');
    },
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    }
  };

  /* ---------- Init ---------- */
  function init() {
    items = readLocal();
    syncFromCustomer();
    notify('init');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
